import React, {
	ChangeEvent,
	useEffect,
	useRef,
	useState,
	useContext,
	useCallback,
} from 'react';

import { SMALL_PADDING } from 'constants/paddings';
import { TextEditorContext } from 'context/text-editor-context/text-editor-context';
import { ActiveLayerContext } from 'context/active-layer-context/active-layer-context';
import { CanvasText } from 'canvas/canvas-text';
import { TextEditorMenu } from './components/text-editor-menu/text-editor-menu';

import styles from './text-editor.module.css';
import { CanvasContext } from 'context/canvas-context/canvas-context';

type Props = {};

/**
 * TextEditor Component
 */
export const TextEditor: React.FC<Props> = () => {
	const {
		textAlign,
		setTextAlign,
		editable,
		text,
		fontSize,
		setText,
		setFontSize,
		bold,
		italic,
		setBold,
		setItalic,
	} = useContext(TextEditorContext);
	const [userCustomFontSize, setUserCustomFontSize] =
		useState<number>(fontSize);
	const { activeLayer } = useContext(ActiveLayerContext);
	const { renderer } = useContext(CanvasContext);
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const [coordinates, setCoordinates] = useState<{ x: number; y: number }>({
		x: 0,
		y: 0,
	});

	const onDoubleClick = useCallback(
		(event: Event) => {
			if (activeLayer && renderer) {
				const e = event as CustomEvent<{
					pageX: number;
					pageY: number;
					transformedPageX: number;
					transformedPageY: number;
				}>;
				const square = activeLayer.getChildByType([
					'rect',
					'rounded-rect',
				]);
				const squareOptions = square?.getOptions();
				const transform = renderer.getTransform();

				const scale =
					transform.scaleX !== transform.initialScale
						? transform.scaleX / transform.initialScale
						: 1;

				const pageX =
					e.detail.pageX +
					(squareOptions.x - e.detail.transformedPageX) * scale;
				const pageY =
					e.detail.pageY +
					(squareOptions.y - e.detail.transformedPageY) * scale;

				setCoordinates({ x: pageX, y: pageY });

				const textChild =
					activeLayer.getChildByType<CanvasText>('text');
				if (textChild) {
					const textOptions = textChild.getOptions();
					textChild.setOptions({
						w: textOptions.w,
						h: textOptions.h,
					});
					setText(textOptions.text);
					setFontSize(textOptions.fontSize);
					setBold(Boolean(/bold/.test(textOptions.style)));
					setItalic(Boolean(/italic/.test(textOptions.style)));
					setTextAlign(textOptions.textAlign);
				}
			}
		},
		[activeLayer, renderer, setCoordinates]
	);

	useEffect(() => {
		window.addEventListener('double-click', onDoubleClick);

		return () => {
			window.removeEventListener('double-click', onDoubleClick);
		};
	}, [activeLayer, renderer, onDoubleClick]);

	useEffect(() => {
		if (textAreaRef?.current && editable) {
			textAreaRef?.current.focus();
		}
	}, [
		editable,
		activeLayer,
		setBold,
		setFontSize,
		setItalic,
		setText,
		setTextAlign,
	]);

	const calculateFontSize = useCallback(
		(fontSize: number) => {
			let nextFontSize = fontSize;
			if (activeLayer && textAreaRef.current) {
				const target = textAreaRef.current;
				const { h: height, scale } = activeLayer.getOptions();

				if (target.scrollHeight * scale > height - SMALL_PADDING) {
					nextFontSize = nextFontSize - 2;
				}

				if (
					target.value.length < text.length &&
					nextFontSize < userCustomFontSize
				) {
					target.style.fontSize = `${nextFontSize + 2}px`;

					if (target.scrollHeight * scale <= height - SMALL_PADDING) {
						nextFontSize = nextFontSize + 2;
					} else {
						target.style.fontSize = `${nextFontSize}px`;
					}
				}
			}
			return nextFontSize;
		},
		[activeLayer, textAreaRef, text.length, userCustomFontSize]
	);

	const onFontSizeChange = (fontSize: number) => {
		setUserCustomFontSize(calculateFontSize(fontSize));
		setFontSize(fontSize);
	};

	const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		if (activeLayer) {
			setFontSize(calculateFontSize(fontSize));

			setText(() => {
				return event.target.value;
			});
		}
	};

	const isVisible = activeLayer && editable;

	if (!activeLayer || !renderer) return null;

	const square = activeLayer.getChildByType(['rect', 'rounded-rect']);
	const squareOptions = square?.getOptions();
	const transform = renderer.getTransform();

	const inverseScale = 1 / (transform.scaleX / transform.initialScale);
	const scale = squareOptions.scale / inverseScale;

	return (
		<>
			{editable && (
				<TextEditorMenu
					pageX={coordinates.x}
					pageY={coordinates.y}
					onFontSizeChange={onFontSizeChange}
					textAreaRef={textAreaRef}
				/>
			)}
			<textarea
				id="test"
				ref={textAreaRef}
				onChange={onChange}
				autoFocus
				className={styles.textarea}
				style={{
					visibility: isVisible ? 'visible' : 'hidden',
					left: coordinates.x + SMALL_PADDING * scale,
					top: coordinates.y + SMALL_PADDING * scale,
					fontSize,
					width: 200 - SMALL_PADDING * 2,
					height: 200 - SMALL_PADDING * 2,
					transform: `scale(${scale})`,
					fontStyle: italic ? 'italic' : '',
					fontWeight: bold ? 800 : 400,
					textAlign,
				}}
				value={text}
			/>
		</>
	);
};
